import { $localize } from '@angular/localize/init';
import 'zone.js';

if (!(window as any).$localize) {
  (window as any).$localize = $localize;
}

import {
  enableProdMode,
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  importProvidersFrom
} from '@angular/core';

import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations, provideNoopAnimations } from '@angular/platform-browser/animations';

import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
  provideHttpClient
} from '@angular/common/http';

import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';

import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';

// Guards and Services
import { AuthGuardService, AdminAuthGuardService } from './app/utils/auth-guard.service';
import { RecaptchaLoaderService } from './app/utils/recaptchaLoader.service';
import { AuthorizationService } from './app/components/authorization/authorization.service';
import { ConfigurationService } from './app/configuration.service';
import { DTFPayoffService } from './app/components/payoff/payoff.service';
import { DTFHomepageService } from './app/components/dtfhomepage/dtfhomepage.service';
import { DTFService } from './app/dtf.service';
import { TranslateUtil } from './app/utils/translate-util';
import { WindowRef } from './app/utils/window';

// Components used in providers
import { FilterPipe } from './app/filter.pipe';
import { FleetMenuItemComponent } from './app/components/fleetservices/layout/menu-item/fleetmenu-item.component';
import { RegistrationPopupComponent } from './app/components/registrationPopup/registrationPopup.component';
import { RegistrationConfirmComponent } from './app/components/registrationConfirm/registrationConfirm.component';
import { PasswordResetComponent } from './app/components/passwordreset/passwordreset.component';
import { ResetpasswordComponent } from './app/resetpassword/resetpassword.component';
import { PasswordResetConfirmComponent } from './app/components/passwordresetconfirm/passwordresetconfirm.component';
import { FleetHeaderComponent } from './app/components/fleetservices/layout/fleetheader.component';
import { FleetPageHeaderComponent } from './app/components/fleetservices/layout/fleetpage-header.component';
import { DynamicLeaseFormComponent } from './app/components/dynamicleaseform/dynamicleaseform.component';
import { OnlinePaymentTableComponent } from './app/components/sharing/online-payment-table/online-payment-table.component';
import { HeaderComponent } from './app/furniture/header.component';
import { FooterComponent } from './app/furniture/footer.component';
import { AlertPageComponent } from './app/furniture/alert.component';
import { VideoComponent } from './app/components/video/video.component';
import { PayComponent } from './app/components/pay/pay.component';
import { ForgotUserComponent } from './app/components/forgotUser/forgotUser.component';
import { MainNavComponent } from './app/furniture/main-nav/main-nav.component';
import { MainNavLinksComponent } from './app/furniture/main-nav-links/main-nav-links.component';

// Routing
import { provideRouter } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Angular Material modules
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';

// Third-party modules
import { NgIdleModule } from '@ng-idle/core';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { NgxPaginationModule } from 'ngx-pagination';

import { AuthInterceptor } from './app/components/interceptor/authinterceptor.service';

// NGX Translate Loader Factory
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/static/assets/i18n/', '.json');  // <-- Absolute path with leading slash
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      // Angular common modules
      HttpClientModule,
      FormsModule,
      ReactiveFormsModule,

      // Angular Material modules
      MatAutocompleteModule,
      MatBadgeModule,
      MatButtonModule,
      MatButtonToggleModule,
      MatCardModule,
      MatCheckboxModule,
      MatChipsModule,
      MatDatepickerModule,
      MatDialogModule,
      MatExpansionModule,
      MatFormFieldModule,
      MatGridListModule,
      MatIconModule,
      MatInputModule,
      MatListModule,
      MatMenuModule,
      MatNativeDateModule,
      MatPaginatorModule,
      MatProgressBarModule,
      MatProgressSpinnerModule,
      MatRadioModule,
      MatRippleModule,
      MatSelectModule,
      MatSidenavModule,
      MatSliderModule,
      MatSlideToggleModule,
      MatSnackBarModule,
      MatSortModule,
      MatStepperModule,
      MatTableModule,
      MatTabsModule,
      MatToolbarModule,
      MatTooltipModule,
      MatTreeModule,

      // 3rd party modules
      NgxPaginationModule,
      NgIdleModule.forRoot(),
      NgIdleKeepaliveModule.forRoot(),

      // TranslateModule with loader
      TranslateModule.forRoot({
        defaultLanguage: 'en-US',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),

    provideRouter(routes),

    // Interceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },

    // App Initializer
    provideAppInitializer(() => {
      const configService = inject(ConfigurationService);
      return configService.loadConfigurationData();
    }),

    // Services & guards
    ConfigurationService,
    WindowRef,
    TranslateUtil,
    DTFService,
    DTFHomepageService,
    DTFPayoffService,
    TranslateService,
    AuthorizationService,
    AuthGuardService,
    AdminAuthGuardService,
    RecaptchaLoaderService,

    // Standalone components/pipes
    FilterPipe,
    FleetMenuItemComponent,
    RegistrationPopupComponent,
    RegistrationConfirmComponent,
    PasswordResetComponent,
    ResetpasswordComponent,
    PasswordResetConfirmComponent,
    FleetHeaderComponent,
    FleetPageHeaderComponent,
    DynamicLeaseFormComponent,
    OnlinePaymentTableComponent,
    HeaderComponent,
    FooterComponent,
    AlertPageComponent,
    VideoComponent,
    PayComponent,
    ForgotUserComponent,
    MainNavComponent,
    MainNavLinksComponent,

    // Animations
    provideAnimations(),
    provideNoopAnimations()
  ]
}).then(ref => {
  const _window = window as any;
  if (_window['ngRef']) {
    _window['ngRef'].destroy();
  }
  _window['ngRef'] = ref;
}).catch(err => console.error(err));
